import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Bell, Building2, CalendarDays, CheckSquare, ChevronDown, ClipboardList, FileSignature,
  Home, Loader2, LogOut, MessageCircle, MoreHorizontal, PanelLeftClose, PanelLeftOpen,
  Settings, Stethoscope, Users, UsersRound,
} from 'lucide-react'
import { PUBLIC_PATHS, ADMIN_PATH_PREFIX, useAuth } from '@/contexts/AuthContext'
import { PAPEL_LABEL } from '@/lib/types'
import { contarNaoLidas } from '@/services/notificacoes'

const NAV = [
  { href: '/', label: 'Hoje', icon: Home },
  { href: '/agenda', label: 'Agenda', icon: CalendarDays },
  { href: '/atendimento', label: 'Atendimento', icon: MessageCircle },
  { href: '/pacientes', label: 'Pacientes', icon: Users },
  { href: '/pendencias', label: 'Pendências', icon: CheckSquare },
]

export const SUB_ATENDIMENTO = [
  { href: '/atendimento', label: 'Caixa de entrada' },
  { href: '/atendimento/agenda-ia', label: 'Agenda IA' },
  { href: '/atendimento/chatbot', label: 'Chatbot' },
  { href: '/configuracoes/whatsapp', label: 'Conexões' },
]

const NAV_DESKTOP_EXTRA = [
  { href: '/protocolos', label: 'Protocolos', icon: ClipboardList },
  { href: '/termos', label: 'Termos', icon: FileSignature },
  { href: '/clinicas', label: 'Clínicas', icon: Building2 },
  { href: '/equipe', label: 'Equipe', icon: UsersRound },
  { href: '/configuracoes', label: 'Configurações', icon: Settings },
]

function isActive(pathname: string, href: string) {
  return href === '/' ? pathname === '/' : pathname.startsWith(href)
}

export default function AppShell({ children }: { children: React.ReactNode }) {
  const {
    loading, user, perfil, current, workspaces, papel, switchWorkspace, signOut,
    adminWorkspaceMode, exitAdminWorkspaceMode, isPlatformAdmin,
  } = useAuth()
  const location = useLocation()
  const pathname = location.pathname
  const navigate = useNavigate()
  const [naoLidas, setNaoLidas] = useState(0)
  const [colapsada, setColapsada] = useState(false)
  const [grupoAberto, setGrupoAberto] = useState(false)

  const isPublic = PUBLIC_PATHS.some(p => pathname.startsWith(p))
  const isAdminRoute = pathname.startsWith(ADMIN_PATH_PREFIX)

  useEffect(() => {
    setColapsada(localStorage.getItem('m360_sidebar_min') === '1')
  }, [])

  function alternarSidebar() {
    setColapsada(v => {
      localStorage.setItem('m360_sidebar_min', v ? '0' : '1')
      return !v
    })
  }

  useEffect(() => {
    if (isPublic || !user) return
    contarNaoLidas().then(setNaoLidas).catch(() => {})
  }, [pathname, isPublic, user])

  if (isPublic || isAdminRoute) return <>{children}</>

  if (loading) {
    return (
      <div className="public-screen">
        <div style={{ textAlign: 'center', color: 'var(--muted)' }}>
          <Loader2 size={28} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
          <div style={{ marginTop: 10, fontSize: 14 }}>Carregando…</div>
        </div>
      </div>
    )
  }

  if (!user) return <>{children}</>

  const min = colapsada ? ' min' : ''

  return (
    <>
      {adminWorkspaceMode && (
        <div
          style={{
            position: 'sticky', top: 0, zIndex: 100,
            background: 'var(--warn-soft)', borderBottom: '1px solid var(--warn)',
            padding: '8px 16px', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
            fontSize: 13,
          }}
        >
          <span>
            <strong>Modo operação Soumed</strong>
            {current ? ` — workspace: ${current.workspace.nome}` : ''}
          </span>
          <button type="button" className="btn-secondary" style={{ minHeight: 32, fontSize: 12.5 }}
            onClick={() => exitAdminWorkspaceMode()}>
            Voltar ao painel admin
          </button>
        </div>
      )}
      {isPlatformAdmin && !adminWorkspaceMode && !isAdminRoute && (
        <div style={{ position: 'fixed', bottom: 12, right: 12, zIndex: 90 }}>
          <Link to="/admin" className="btn-secondary" style={{ minHeight: 36, fontSize: 13, boxShadow: 'var(--shadow-float)' }}>
            Painel admin
          </Link>
        </div>
      )}
      <aside className={`m360-sidebar${min}`}>
        <div className="m360-sidebar-topo">
          <div
            aria-hidden
            style={{
              width: 34, height: 34, borderRadius: 9, background: 'var(--primary)', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
            }}
          >
            <Stethoscope size={18} />
          </div>
          <div className="sidebar-label">
            <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: '-0.02em' }}>Médico 360</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Sua rotina sob controle</div>
          </div>
        </div>

        <button
          className="m360-sidebar-toggle"
          onClick={() => alternarSidebar()}
          aria-label={colapsada ? 'Expandir menu' : 'Minimizar menu'}
          aria-expanded={!colapsada}
          title={colapsada ? 'Expandir menu' : 'Minimizar menu'}
        >
          {colapsada ? <PanelLeftOpen size={16} aria-hidden /> : <PanelLeftClose size={16} aria-hidden />}
          <span className="sidebar-label">Minimizar</span>
        </button>

        {workspaces.length > 1 && !colapsada && (
          <select
            className="field-input"
            style={{ marginBottom: 14, minHeight: 38, fontSize: 13.5 }}
            value={current?.workspace.id ?? ''}
            onChange={e => switchWorkspace(e.target.value)}
            aria-label="Trocar workspace"
          >
            {workspaces.map(w => (
              <option key={w.workspace.id} value={w.workspace.id}>{w.workspace.nome}</option>
            ))}
          </select>
        )}

        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          {[...NAV, ...NAV_DESKTOP_EXTRA].map(item => {
            const Icon = item.icon

            if (item.href === '/atendimento') {
              const noGrupo = SUB_ATENDIMENTO.some(s => pathname === s.href)
                || pathname.startsWith('/atendimento')
              const aberto = !colapsada && (grupoAberto || noGrupo)
              return (
                <div key={item.href}>
                  <button
                    className={`sidebar-item${noGrupo ? ' active' : ''}`}
                    style={{ width: '100%', background: noGrupo ? undefined : 'none', border: 'none', textAlign: 'left' }}
                    onClick={() => {
                      if (colapsada) { navigate('/atendimento'); return }
                      setGrupoAberto(v => !v)
                    }}
                    aria-expanded={aberto}
                    title={colapsada ? item.label : undefined}
                  >
                    <Icon size={17} aria-hidden style={{ flexShrink: 0 }} />
                    <span className="sidebar-label">{item.label}</span>
                    <ChevronDown
                      size={15}
                      aria-hidden
                      className="sidebar-label"
                      style={{
                        marginLeft: 'auto',
                        transition: 'transform .15s',
                        transform: aberto ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  </button>
                  {aberto && (
                    <div className="sidebar-sub">
                      {SUB_ATENDIMENTO.map(s => (
                        <Link
                          key={s.href}
                          to={s.href}
                          className={`sidebar-subitem${pathname === s.href ? ' active' : ''}`}
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            }

            return (
              <Link
                key={item.href}
                to={item.href}
                className={`sidebar-item${isActive(pathname, item.href) ? ' active' : ''}`}
                title={colapsada ? item.label : undefined}
              >
                <Icon size={17} aria-hidden style={{ flexShrink: 0 }} />
                <span className="sidebar-label">{item.label}</span>
              </Link>
            )
          })}
          <Link
            to="/notificacoes"
            className={`sidebar-item${isActive(pathname, '/notificacoes') ? ' active' : ''}`}
            title={colapsada ? 'Notificações' : undefined}
            style={{ position: 'relative' }}
          >
            <Bell size={17} aria-hidden style={{ flexShrink: 0 }} />
            <span className="sidebar-label">Notificações</span>
            {naoLidas > 0 && (
              <span
                className="badge badge-danger sidebar-contador"
                style={{ fontSize: 11, padding: '1px 7px' }}
              >
                {naoLidas}
              </span>
            )}
          </Link>
        </nav>

        <div className="m360-sidebar-rodape">
          <div className="sidebar-label">
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{perfil?.nome ?? user.email}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
              {papel ? PAPEL_LABEL[papel] : ''}
            </div>
          </div>
          <button
            className="btn-secondary"
            style={{ width: '100%', minHeight: 38, fontSize: 13.5, padding: colapsada ? 0 : undefined }}
            onClick={() => signOut()}
            title={colapsada ? 'Sair' : undefined}
            aria-label="Sair"
          >
            <LogOut size={15} aria-hidden />
            <span className="sidebar-label">Sair</span>
          </button>
        </div>
      </aside>

      <main
        className={`m360-main${min}${pathname.startsWith('/atendimento') ? ' m360-main--full' : ''}`}
      >
        {children}
      </main>

      <nav className="bottom-nav" aria-label="Navegação principal">
        {NAV.map(item => {
          const Icon = item.icon
          const active = isActive(pathname, item.href)
          return (
            <Link
              key={item.href}
              to={item.href}
              className={`bottom-nav-item${active ? ' active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <Icon size={21} aria-hidden />
              {item.label}
            </Link>
          )
        })}
        <button
          type="button"
          className={`bottom-nav-item${isActive(pathname, '/mais') ? ' active' : ''}`}
          style={{ background: 'none', border: 'none', position: 'relative' }}
          onClick={() => navigate('/mais')}
        >
          <MoreHorizontal size={21} aria-hidden />
          Mais
          {naoLidas > 0 && (
            <span
              aria-label={`${naoLidas} notificações não lidas`}
              style={{
                position: 'absolute', top: 8, right: '26%', minWidth: 16, height: 16,
                borderRadius: 999, background: 'var(--danger)', color: '#fff',
                fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center',
                justifyContent: 'center', padding: '0 4px',
              }}
            >
              {naoLidas}
            </span>
          )}
        </button>
      </nav>
    </>
  )
}
